'use client';

import { c as _c } from "react/compiler-runtime";
import { Select } from '@/features/payload-cms/components/form/select';
import { Text } from '@/features/payload-cms/components/form/text';
import type { VisitDaySelectionBlock } from '@/features/payload-cms/components/form/types';
import type { VisitDayChoice } from '@/features/visit-day/components/visit-day-choice-list';
import { VisitDayChoiceList } from '@/features/visit-day/components/visit-day-choice-list';
import { formatVisitDay } from '@/features/visit-day/format-visit-day';
import { EXTERNAL_VISIT, VISITING_CHILD, WITH_LUNCH, WITHOUT_LUNCH } from '@/features/visit-day/visit-day-choices';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { trpc } from '@/trpc/client';
import type { Locale, StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import type React from 'react';
import type { Control, FieldError, FieldErrorsImpl, FieldValues, Merge, UseFormRegister } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

/** The free places are read again this often while the form is open. */
const PLACES_REFRESH_MS = 60_000;

// relatives know the Abteilung of the child they visit, not its Hof: a Hof is named after its
// Abteilungen, so the list of Höfe reads as a list of Abteilungen
const placeholderText: StaticTranslationString = {
  de: 'Abteilung auswählen',
  en: 'Select a local group',
  fr: 'Choisir un groupe local'
};
const loadingText: StaticTranslationString = {
  de: 'Abteilungen werden geladen …',
  en: 'Loading local groups …',
  fr: 'Chargement des groupes locaux …'
};
const loadErrorText: StaticTranslationString = {
  de: 'Die Abteilungen konnten nicht geladen werden. Prüfe deine Verbindung.',
  en: 'The local groups could not be loaded. Check your connection.',
  fr: 'Les groupes locaux n’ont pas pu être chargés. Vérifie ta connexion.'
};
const noHofOptionText: StaticTranslationString = {
  de: 'Kein Bezug zu einer Abteilung',
  en: 'No connection to a local group',
  fr: 'Aucun lien avec un groupe local'
};
const visitorTypeLegend: StaticTranslationString = {
  de: 'Wen besucht ihr?',
  en: 'Who are you visiting?',
  fr: 'Qui venez-vous voir ?'
};
const visitingChildText: StaticTranslationString = {
  de: 'Ein Kind im Lager, z.B. unser Kind, Göttikind oder Enkelkind',
  en: 'A child at the camp, e.g. our child, godchild or grandchild',
  fr: 'Un enfant au camp, p. ex. notre enfant, filleul·e ou petit-enfant'
};
const externalVisitText: StaticTranslationString = {
  de: 'Niemand Bestimmtes, wir kommen ohne Bezug zu einer Abteilung',
  en: 'No one in particular, we come without a connection to a local group',
  fr: 'Personne en particulier, nous venons sans lien avec un groupe local'
};
const childNameLabel: StaticTranslationString = {
  de: 'Vor- und Nachname des Kindes (optional)',
  en: 'First and last name of the child (optional)',
  fr: 'Prénom et nom de l’enfant (facultatif)'
};
const childNamePlaceholder: StaticTranslationString = {
  de: 'z.B. Lea Muster',
  en: 'e.g. Lea Muster',
  fr: 'p. ex. Lea Muster'
};
const personsLegend: StaticTranslationString = {
  de: 'Anzahl Personen',
  en: 'Number of persons',
  fr: 'Nombre de personnes'
};
const personsText = (count: number, locale: Locale): string => {
  const texts: Record<Locale, string> = {
    de: count === 1 ? '1 Person' : `${count} Personen`,
    en: count === 1 ? '1 person' : `${count} persons`,
    fr: count === 1 ? '1 personne' : `${count} personnes`
  };
  return texts[locale];
};
const lunchLegend: StaticTranslationString = {
  de: 'Mittagessen im Lager',
  en: 'Lunch at the camp',
  fr: 'Repas de midi au camp'
};
const withLunchOptionText: StaticTranslationString = {
  de: 'Ja, wir essen im Lager zu Mittag',
  en: 'Yes, we have lunch at the camp',
  fr: 'Oui, nous mangeons à midi au camp'
};
const withoutLunchOptionText: StaticTranslationString = {
  de: 'Nein, wir verpflegen uns selbst',
  en: 'No, we bring our own lunch',
  fr: 'Non, nous apportons notre repas'
};
const chooseDayLegend: StaticTranslationString = {
  de: 'Besuchstag wählen',
  en: 'Choose the visiting day',
  fr: 'Choisir la journée des visites'
};
const yourDayText: StaticTranslationString = {
  de: 'Euer Besuchstag',
  en: 'Your visiting day',
  fr: 'Votre journée des visites'
};
const fullText: StaticTranslationString = {
  de: 'ausgebucht',
  en: 'fully booked',
  fr: 'complet'
};
const tooFewPlacesText: StaticTranslationString = {
  de: 'Für diese Wahl sind nicht mehr genug Plätze frei.',
  en: 'Not enough places are left for this choice.',
  fr: 'Il ne reste plus assez de places pour ce choix.'
};
const placesText = (free: number, locale: Locale): string => {
  if (free === 0) return fullText[locale];
  const texts: Record<Locale, string> = {
    de: free === 1 ? '1 Platz frei' : `${free} Plätze frei`,
    en: free === 1 ? '1 place left' : `${free} places left`,
    fr: free === 1 ? '1 place libre' : `${free} places libres`
  };
  return texts[locale];
};

/** The free places of a day, or that it has too few, in the colour that says which. */
const PlacesText: React.FC<{
  free: number;
  persons: number;
  locale: Locale;
}> = t0 => {
  const $ = _c(6);
  const {
    free,
    persons,
    locale
  } = t0;
  const t1 = free < persons ? "text-danger-text font-semibold" : "text-green-700";
  let t2;
  if ($[0] !== free || $[1] !== locale) {
    t2 = placesText(free, locale);
    $[0] = free;
    $[1] = locale;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

/** One day's places as the form shows them. */
interface DayPlaces {
  date: string;
  free: number;
  freeWithoutLunch: number;
}
const isFieldName = (name: string | null | undefined): name is string => typeof name === 'string' && name !== '';

/**
 * The whole Besuchstag choice, in the order in which each answer limits the next. Relatives
 * first say whom they visit: those visiting a child pick its Hof, whose Quartier sets the day,
 * and may name the child; visitors without a Hof pick the day. Then come the number of persons
 * and lunch, where each kind has places of its own. A choice without enough free places for the
 * persons is disabled; the server checks the places again on sending. Each question shows only
 * where the form names an answer for it, so an older form keeps its own fields.
 */
export const VisitDaySelection: React.FC<VisitDaySelectionBlock & {
  control: Control;
  registerAction: UseFormRegister<string & FieldValues>;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>>;
}> = t0 => {
  const $ = _c(148);
  const {
    name,
    label,
    required,
    dayField,
    lunchField,
    personCountField,
    visitorTypeField,
    childNameField,
    maxPersons,
    control,
    registerAction,
    error
  } = t0;
  const locale = useCurrentLocale(i18nConfig) as Locale;
  const {
    setValue,
    getValues
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const hoefe = trpc.visitDay.getVisitDayHofList.useQuery(undefined, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      refetchOnMount: true,
      refetchInterval: PLACES_REFRESH_MS
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const days = trpc.visitDay.getVisitDayList.useQuery(undefined, t2);
  let t3;
  if ($[2] !== visitorTypeField) {
    t3 = isFieldName(visitorTypeField);
    $[2] = visitorTypeField;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const asksVisitorType = t3;
  let t4;
  if ($[4] !== lunchField) {
    t4 = isFieldName(lunchField);
    $[4] = lunchField;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const asksLunch = t4;
  const asksPersons = typeof maxPersons === "number" && maxPersons >= 1;
  const t5 = asksLunch ? lunchField : name;
  const t6 = asksVisitorType ? visitorTypeField : name;
  let t7;
  if ($[6] !== dayField || $[7] !== name || $[8] !== personCountField || $[9] !== t5 || $[10] !== t6) {
    t7 = [name, t5, dayField, personCountField, t6];
    $[6] = dayField;
    $[7] = name;
    $[8] = personCountField;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== control || $[13] !== t7) {
    t8 = {
      control,
      name: t7
    };
    $[12] = control;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const [selectedHofId, lunch, pickedDay, persons, visitorType] = useWatch(t8) as [unknown, unknown, unknown, unknown, unknown];
  const isExternal = asksVisitorType ? visitorType === EXTERNAL_VISIT : selectedHofId === EXTERNAL_VISIT;
  const showsHof = asksVisitorType ? visitorType === VISITING_CHILD : true;
  let t9;
  if ($[15] !== hoefe.data || $[16] !== isExternal || $[17] !== pickedDay || $[18] !== selectedHofId) {
    t9 = isExternal ? pickedDay : (hoefe.data ?? []).find(hof => hof.id === selectedHofId)?.date;
    $[15] = hoefe.data;
    $[16] = isExternal;
    $[17] = pickedDay;
    $[18] = selectedHofId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const ownDay = t9;
  const unavailable = hoefe.data === undefined && (hoefe.isError || hoefe.fetchStatus === "paused");
  let t10;
  if ($[20] !== days.data) {
    t10 = days.data ?? [];
    $[20] = days.data;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let dayChoices;
  let dayPlaces;
  let freeOfKind;
  let lunchChoices;
  let personChoices;
  let personCount;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let visitDay;
  if ($[22] !== asksLunch || $[23] !== asksPersons || $[24] !== asksVisitorType || $[25] !== childNameField || $[26] !== control || $[27] !== error || $[28] !== getValues || $[29] !== hoefe.data || $[30] !== hoefe.isLoading || $[31] !== isExternal || $[32] !== label || $[33] !== locale || $[34] !== lunch || $[35] !== maxPersons || $[36] !== name || $[37] !== ownDay || $[38] !== persons || $[39] !== registerAction || $[40] !== required || $[41] !== setValue || $[42] !== showsHof || $[43] !== t10 || $[44] !== unavailable || $[45] !== visitorTypeField) {
    dayPlaces = t10.map(_temp);
    let t16;
    if ($[58] !== ownDay) {
      t16 = day_0 => day_0.date === ownDay;
      $[58] = ownDay;
      $[59] = t16;
    } else {
      t16 = $[59];
    }
    visitDay = dayPlaces.find(t16);
    const chosenPersons = Number(persons);
    let t17;
    if ($[60] !== chosenPersons) {
      t17 = Number.isInteger(chosenPersons) && chosenPersons > 1 ? chosenPersons : 1;
      $[60] = chosenPersons;
      $[61] = t17;
    } else {
      t17 = $[61];
    }
    personCount = t17;
    freeOfKind = _temp2;
    let t18;
    if ($[62] !== asksLunch) {
      t18 = day_2 => asksLunch ? Math.max(day_2.free, day_2.freeWithoutLunch) : day_2.free;
      $[62] = asksLunch;
      $[63] = t18;
    } else {
      t18 = $[63];
    }
    const freeOfBestKind = t18;
    let t19;
    if ($[64] !== asksLunch || $[65] !== freeOfBestKind || $[66] !== lunch) {
      t19 = day_3 => {
        if (!asksLunch || lunch === WITH_LUNCH) {
          return day_3.free;
        }
        if (lunch === WITHOUT_LUNCH) {
          return day_3.freeWithoutLunch;
        }
        return freeOfBestKind(day_3);
      };
      $[64] = asksLunch;
      $[65] = freeOfBestKind;
      $[66] = lunch;
      $[67] = t19;
    } else {
      t19 = $[67];
    }
    const freeOfChosenKind = t19;
    let t20;
    if ($[68] !== hoefe.data) {
      t20 = hoefe.data ?? [];
      $[68] = hoefe.data;
      $[69] = t20;
    } else {
      t20 = $[69];
    }
    let t21;
    if ($[70] !== asksVisitorType || $[71] !== hoefe.data || $[72] !== locale || $[73] !== t20) {
      const hofOptions = t20.map(_temp3);
      t21 = hoefe.data === undefined || asksVisitorType ? hofOptions : [...hofOptions, {
        value: EXTERNAL_VISIT,
        label: noHofOptionText[locale]
      }];
      $[70] = asksVisitorType;
      $[71] = hoefe.data;
      $[72] = locale;
      $[73] = t20;
      $[74] = t21;
    } else {
      t21 = $[74];
    }
    const options = t21;
    let t22;
    if ($[75] !== childNameField || $[76] !== getValues || $[77] !== name || $[78] !== setValue) {
      t22 = value => {
        if (value === EXTERNAL_VISIT) {
          setValue(name, EXTERNAL_VISIT);
          if (isFieldName(childNameField)) {
            setValue(childNameField, "");
          }
        } else {
          if (getValues(name) === EXTERNAL_VISIT) {
            setValue(name, "");
          }
        }
      };
      $[75] = childNameField;
      $[76] = getValues;
      $[77] = name;
      $[78] = setValue;
      $[79] = t22;
    } else {
      t22 = $[79];
    }
    const onVisitorTypeChosen = t22;
    const t23 = visitingChildText[locale];
    let t24;
    if ($[80] !== t23) {
      t24 = {
        value: VISITING_CHILD,
        label: t23
      };
      $[80] = t23;
      $[81] = t24;
    } else {
      t24 = $[81];
    }
    const t25 = externalVisitText[locale];
    let t26;
    if ($[82] !== t25) {
      t26 = {
        value: EXTERNAL_VISIT,
        label: t25
      };
      $[82] = t25;
      $[83] = t26;
    } else {
      t26 = $[83];
    }
    let t27;
    if ($[84] !== t24 || $[85] !== t26) {
      t27 = [t24, t26];
      $[84] = t24;
      $[85] = t26;
      $[86] = t27;
    } else {
      t27 = $[86];
    }
    const visitorTypeChoices = t27;
    let t28;
    if ($[87] !== asksLunch || $[88] !== freeOfChosenKind || $[89] !== locale || $[90] !== personCount) {
      t28 = day_4 => {
        const free = freeOfChosenKind(day_4);
        return {
          value: day_4.date,
          label: formatVisitDay(day_4.date, locale),
          disabled: free < personCount,
          ...(!asksLunch || free < personCount ? {
            sub: <PlacesText free={free} persons={personCount} locale={locale} />
          } : {})
        };
      };
      $[87] = asksLunch;
      $[88] = freeOfChosenKind;
      $[89] = locale;
      $[90] = personCount;
      $[91] = t28;
    } else {
      t28 = $[91];
    }
    dayChoices = dayPlaces.map(t28);
    personChoices = Array.from({
      length: asksPersons ? maxPersons : 0
    }, (_, index) => {
      const count = index + 1;
      return {
        value: String(count),
        label: personsText(count, locale),
        disabled: visitDay !== undefined && freeOfBestKind(visitDay) < count
      };
    });
    lunchChoices = [{
      withLunch: true,
      value: WITH_LUNCH,
      label: withLunchOptionText[locale]
    }, {
      withLunch: false,
      value: WITHOUT_LUNCH,
      label: withoutLunchOptionText[locale]
    }].map(t29 => {
      const {
        withLunch: withLunch_0,
        value: value_0,
        label: choiceLabel
      } = t29;
      if (visitDay === undefined) {
        return {
          value: value_0,
          label: choiceLabel
        };
      }
      const free_0 = freeOfKind(visitDay, withLunch_0);
      return {
        value: value_0,
        label: choiceLabel,
        sub: <PlacesText free={free_0} persons={personCount} locale={locale} />,
        disabled: free_0 < personCount
      };
    });
    t11 = "mb-4";
    if ($[92] !== asksVisitorType || $[93] !== control || $[94] !== locale || $[95] !== onVisitorTypeChosen || $[96] !== visitorTypeChoices || $[97] !== visitorTypeField) {
      t12 = asksVisitorType && <VisitDayChoiceList control={control} name={visitorTypeField} legend={visitorTypeLegend[locale]} choices={visitorTypeChoices} onChosen={onVisitorTypeChosen} />;
      $[92] = asksVisitorType;
      $[93] = control;
      $[94] = locale;
      $[95] = onVisitorTypeChosen;
      $[96] = visitorTypeChoices;
      $[97] = visitorTypeField;
      $[98] = t12;
    } else {
      t12 = $[98];
    }
    if ($[99] !== control || $[100] !== error || $[101] !== hoefe.isLoading || $[102] !== label || $[103] !== locale || $[104] !== name || $[105] !== options || $[106] !== registerAction || $[107] !== required || $[108] !== showsHof || $[109] !== unavailable) {
      t13 = showsHof && <Select blockType="select" name={name} label={label ?? ""} required={required ?? false} control={control} registerAction={registerAction} {...error === undefined ? {} : {
        error
      }} optionType="dropdown" allowMultiple={false} placeholder={unavailable ? loadErrorText[locale] : (hoefe.isLoading ? loadingText : placeholderText)[locale]} options={options} />;
      $[99] = control;
      $[100] = error;
      $[101] = hoefe.isLoading;
      $[102] = label;
      $[103] = locale;
      $[104] = name;
      $[105] = options;
      $[106] = registerAction;
      $[107] = required;
      $[108] = showsHof;
      $[109] = unavailable;
      $[110] = t13;
    } else {
      t13 = $[110];
    }
    if ($[111] !== childNameField || $[112] !== isExternal || $[113] !== locale || $[114] !== registerAction || $[115] !== showsHof) {
      t14 = showsHof && !isExternal && isFieldName(childNameField) && <Text blockType="text" name={childNameField} label={childNameLabel[locale]} required={false} placeholder={childNamePlaceholder[locale]} registerAction={registerAction} />;
      $[111] = childNameField;
      $[112] = isExternal;
      $[113] = locale;
      $[114] = registerAction;
      $[115] = showsHof;
      $[116] = t14;
    } else {
      t14 = $[116];
    }
    t15 = visitDay !== undefined && !isExternal && <div className="border-line bg-tint mb-4 rounded-[14px] border px-4 py-3"><p className="m-0 text-xs font-semibold tracking-wide text-green-700 uppercase">{yourDayText[locale]}</p><span className="text-heading mt-1 block text-[15px] font-medium">{formatVisitDay(visitDay.date, locale)}</span>{(!asksLunch || freeOfBestKind(visitDay) < personCount) && <span className="mt-1 block text-sm"><PlacesText free={freeOfBestKind(visitDay)} persons={personCount} locale={locale} /></span>}</div>;
    $[22] = asksLunch;
    $[23] = asksPersons;
    $[24] = asksVisitorType;
    $[25] = childNameField;
    $[26] = control;
    $[27] = error;
    $[28] = getValues;
    $[29] = hoefe.data;
    $[30] = hoefe.isLoading;
    $[31] = isExternal;
    $[32] = label;
    $[33] = locale;
    $[34] = lunch;
    $[35] = maxPersons;
    $[36] = name;
    $[37] = ownDay;
    $[38] = persons;
    $[39] = registerAction;
    $[40] = required;
    $[41] = setValue;
    $[42] = showsHof;
    $[43] = t10;
    $[44] = unavailable;
    $[45] = visitorTypeField;
    $[46] = dayChoices;
    $[47] = dayPlaces;
    $[48] = freeOfKind;
    $[49] = lunchChoices;
    $[50] = personChoices;
    $[51] = personCount;
    $[52] = t11;
    $[53] = t12;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = visitDay;
  } else {
    dayChoices = $[46];
    dayPlaces = $[47];
    freeOfKind = $[48];
    lunchChoices = $[49];
    personChoices = $[50];
    personCount = $[51];
    t11 = $[52];
    t12 = $[53];
    t13 = $[54];
    t14 = $[55];
    t15 = $[56];
    visitDay = $[57];
  }
  let t16;
  if ($[117] !== control || $[118] !== dayChoices || $[119] !== dayField || $[120] !== dayPlaces || $[121] !== isExternal || $[122] !== locale) {
    t16 = isExternal && dayPlaces.length > 0 && <VisitDayChoiceList control={control} name={dayField} legend={chooseDayLegend[locale]} choices={dayChoices} />;
    $[117] = control;
    $[118] = dayChoices;
    $[119] = dayField;
    $[120] = dayPlaces;
    $[121] = isExternal;
    $[122] = locale;
    $[123] = t16;
  } else {
    t16 = $[123];
  }
  let t17;
  if ($[124] !== asksPersons || $[125] !== control || $[126] !== locale || $[127] !== personChoices || $[128] !== personCountField) {
    t17 = asksPersons && <VisitDayChoiceList control={control} name={personCountField} legend={personsLegend[locale]} choices={personChoices} />;
    $[124] = asksPersons;
    $[125] = control;
    $[126] = locale;
    $[127] = personChoices;
    $[128] = personCountField;
    $[129] = t17;
  } else {
    t17 = $[129];
  }
  let t18;
  if ($[130] !== asksLunch || $[131] !== control || $[132] !== freeOfKind || $[133] !== locale || $[134] !== lunchChoices || $[135] !== lunchField || $[136] !== personCount || $[137] !== visitDay) {
    t18 = asksLunch && <VisitDayChoiceList control={control} name={lunchField} legend={lunchLegend[locale]} choices={lunchChoices} validate={value_1 => {
      if (visitDay === undefined) {
        return true;
      }
      const free_1 = freeOfKind(visitDay, value_1 === WITH_LUNCH);
      return free_1 >= personCount || tooFewPlacesText[locale];
    }} />;
    $[130] = asksLunch;
    $[131] = control;
    $[132] = freeOfKind;
    $[133] = locale;
    $[134] = lunchChoices;
    $[135] = lunchField;
    $[136] = personCount;
    $[137] = visitDay;
    $[138] = t18;
  } else {
    t18 = $[138];
  }
  let t19;
  if ($[139] !== t11 || $[140] !== t12 || $[141] !== t13 || $[142] !== t14 || $[143] !== t15 || $[144] !== t16 || $[145] !== t17 || $[146] !== t18) {
    t19 = <div className={t11}>{t12}{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[139] = t11;
    $[140] = t12;
    $[141] = t13;
    $[142] = t14;
    $[143] = t15;
    $[144] = t16;
    $[145] = t17;
    $[146] = t18;
    $[147] = t19;
  } else {
    t19 = $[147];
  }
  return t19;
};
function _temp(day) {
  return {
    date: day.date,
    free: day.free,
    freeWithoutLunch: day.freeWithoutLunch as number | undefined ?? 0
  };
}
function _temp2(day_1, withLunch) {
  return withLunch ? day_1.free : day_1.freeWithoutLunch;
}
function _temp3(hof_0) {
  return {
    value: hof_0.id,
    label: hof_0.name
  };
}